// src/pages/brand/BrandProjects.tsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { api, apiFetch, type SourceMode } from "../../lib/api";

// ✅ Context del AppShell (buscador + créditos reales)
import type { ModeShellContext } from "../../components/layout/AppShell";

function formatDateISO(iso: string) {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  } catch {
    return "—";
  }
}

function Chip({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-[11px] px-3 py-2 rounded-xl border border-white/10 bg-black/30 text-gray-200 whitespace-nowrap">
      {children}
    </span>
  );
}

// ✅ Botones con tamaño consistente
const CTA_BASE =
  "h-10 px-4 rounded-xl text-sm font-semibold inline-flex items-center justify-center whitespace-nowrap";

function PrimaryButton({
  children,
  onClick,
  disabled,
  title,
  className = "",
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  title?: string;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`${CTA_BASE}
        bg-gradient-to-r from-indigo-500 to-purple-500
        hover:from-indigo-400 hover:to-purple-400 transition shadow-lg shadow-indigo-500/30
        disabled:opacity-50 disabled:cursor-not-allowed
        ${className}`}
    >
      {children}
    </button>
  );
}

function SecondaryButton({
  children,
  onClick,
  disabled,
  title,
  className = "",
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  title?: string;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`${CTA_BASE}
        border border-white/15 hover:bg-white/10 transition
        disabled:opacity-50 disabled:cursor-not-allowed
        ${className}`}
    >
      {children}
    </button>
  );
}

function Modal({
  open,
  title,
  children,
  onClose,
}: {
  open: boolean;
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0c0c14] p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-gray-400">Nuevo proyecto</div>
            <div className="text-lg font-bold text-white mt-2">{title}</div>
            <p className="text-sm text-gray-400 mt-2">Crea una carpeta para organizar.</p>
          </div>

          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg border border-white/10 hover:bg-white/10 transition text-xs text-gray-200"
          >
            ✕
          </button>
        </div>

        <div className="mt-5">{children}</div>
      </div>
    </div>
  );
}

type UiProject = {
  id: string;
  name: string;
  created_at: string;
  itemsCount: number;
};

const MODE: SourceMode = "brand";

export default function BrandProjects() {
  const navigate = useNavigate();
  const { user, getAccessToken } = useAuth() as any;

  // ✅ viene del AppShell (topbar)
  const { searchQuery, credits } = useOutletContext<ModeShellContext>();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [projects, setProjects] = useState<UiProject[]>([]);

  // Modal create
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");

  // (placeholder)
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState("");

  // toast (simple)
  const [msg, setMsg] = useState("");

  const authHeaders = useCallback(async () => {
    const token = await getAccessToken?.();
    if (!token) throw new Error("No hay sesión activa.");
    return { Authorization: `Bearer ${token}` };
  }, [getAccessToken]);

  const getDefaultBrandName = useCallback(() => {
    const email = String(user?.email || "").trim();
    if (email) return email.split("@")[0] || "Mi marca";
    return "Mi marca";
  }, [user]);

  const load = useCallback(async () => {
    setError("");
    setLoading(true);
    try {
      const headers = await authHeaders();

      // 1) Proyectos SOLO brand
      const pr = await api.projectsList({ sourceMode: MODE, limit: 200 }, { headers });
      if (!pr.success) throw new Error(pr.error || "No se pudieron cargar proyectos.");

      const base: UiProject[] = (pr.projects || []).map((p: any) => ({
        id: p.id,
        name: p.name,
        created_at: p.created_at,
        itemsCount: 0,
      }));

      // 2) Conteo de creaciones por project_id SOLO brand
      const gr = await api.generationsList({ sourceMode: MODE }, { headers });
      const counts = new Map<string, number>();

      if (gr.success) {
        for (const it of gr.items || []) {
          const pid = it.project_id || "";
          if (!pid) continue;
          counts.set(pid, (counts.get(pid) || 0) + 1);
        }
      }

      const merged = base.map((p) => ({ ...p, itemsCount: counts.get(p.id) || 0 }));
      setProjects(merged);
    } catch (e: any) {
      setError(e?.message || "Error cargando proyectos.");
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    if (!user) return;
    load();
  }, [user, load]);

  // ✅ Filtro usando el buscador del AppShell
  const filtered = useMemo(() => {
    const q = String(searchQuery || "").trim().toLowerCase();
    if (!q) return projects;
    return projects.filter((p) => p.name.toLowerCase().includes(q));
  }, [projects, searchQuery]);

  const openCreate = () => {
    setNewName("");
    setNewDesc("");
    setCreateOpen(true);
  };

  const doCreate = async () => {
    try {
      setError("");
      const name = newName.trim() || `Proyecto ${projects.length + 1}`;
      const headers = await authHeaders();

      const res = await api.createProject({ name, sourceMode: MODE }, { headers });
      if (!res.success) throw new Error(res.error || "No se pudo crear.");

      setMsg("✅ Proyecto creado");
      setTimeout(() => setMsg(""), 1200);
      setCreateOpen(false);

      await load();
    } catch (e: any) {
      setMsg(e?.message || "⚠️ No se pudo crear");
      setTimeout(() => setMsg(""), 1500);
    }
  };

  const doDelete = async (id: string) => {
    const ok = window.confirm("¿Eliminar este proyecto? Esta acción no se puede deshacer.");
    if (!ok) return;

    try {
      setError("");
      const headers = await authHeaders();

      const resp = await apiFetch<{ success: boolean; error?: string }>(`/api/projects/${id}`, {
        method: "DELETE",
        headers,
      });

      if (!resp.success) throw new Error(resp.error || "No se pudo eliminar.");

      setMsg("🗑️ Eliminado");
      setTimeout(() => setMsg(""), 1200);

      await load();
    } catch (e: any) {
      setMsg(e?.message || "⚠️ No se pudo eliminar");
      setTimeout(() => setMsg(""), 1500);
    }
  };

  const openProjectGallery = (p: UiProject) => {
    navigate(`/brand/projects/${encodeURIComponent(p.id)}`, { replace: false });
  };

  const openProjectGenerator = (p: UiProject) => {
    navigate(`/brand/create?projectId=${encodeURIComponent(p.id)}`, { replace: false });
  };

  return (
    <section className="max-w-5xl">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-gray-400">BRAND MODE</div>

          <h1 className="text-2xl md:text-3xl font-bold mt-2">Organiza tus creaciones</h1>
          <p className="text-gray-300/90 mt-2 text-sm md:text-base max-w-2xl leading-relaxed">
            Crea “carpetas” por campaña, cliente o formato. Así puedes encontrar y repetir resultados en minutos.
          </p>

          {msg && <div className="mt-3 text-[12px] text-emerald-300">{msg}</div>}
          {error && <div className="mt-3 text-[12px] text-rose-300">{error}</div>}
        </div>

        <div className="flex flex-col gap-2 items-end">
          <PrimaryButton onClick={openCreate} className="min-w-[180px]" disabled={loading}>
            📁 Nuevo proyecto
          </PrimaryButton>

          <SecondaryButton onClick={() => navigate("/brand/create")} className="min-w-[180px]" disabled={loading}>
            ✨ Ir al generador
          </SecondaryButton>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2">
        <button
          onClick={load}
          className="text-[11px] px-3 py-1.5 rounded-lg border border-white/10 hover:bg-white/10 transition text-gray-200"
          title="Refrescar"
          disabled={loading}
        >
          {loading ? "⏳ Cargando..." : "🔄 Refrescar proyectos"}
        </button>

        {!!String(searchQuery || "").trim() && (
          <span className="text-[11px] text-gray-500">Tip: limpia “Buscar…” arriba para ver todo.</span>
        )}
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        {filtered.map((p) => (
          <div
            key={p.id}
            className="rounded-2xl border border-white/10 bg-white/5 p-5 hover:bg-white/10 transition"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate">{p.name}</div>
                <div className="text-[11px] text-gray-500 mt-1">Creado: {formatDateISO(p.created_at)}</div>

                {/* ✅ Todo alineado al mismo nivel que la basura */}
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => openProjectGallery(p)}
                    className="text-[11px] px-3 py-2 rounded-xl border border-white/10 hover:bg-white/10 transition text-gray-200"
                    title="Ver imágenes del proyecto"
                  >
                    🖼️ Ver
                  </button>

                  <button
                    type="button"
                    onClick={() => openProjectGenerator(p)}
                    className="text-[11px] px-3 py-2 rounded-xl border border-white/10 hover:bg-white/10 transition text-gray-200"
                    title="Generar dentro de este proyecto"
                  >
                    ✨ Usar
                  </button>

                  <Chip>
                    {p.itemsCount} creación{p.itemsCount === 1 ? "" : "es"}
                  </Chip>

                  <button
                    type="button"
                    onClick={() => doDelete(p.id)}
                    className="text-[11px] px-3 py-2 rounded-xl border border-white/10 hover:bg-white/10 transition text-gray-300"
                    title="Eliminar proyecto"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}

        {filtered.length === 0 && !loading && (
          <div className="col-span-full rounded-2xl border border-white/10 bg-white/5 p-8 text-center text-gray-400">
            <div className="text-base font-semibold text-gray-200">Aún no tienes proyectos</div>
            <p className="text-sm mt-2">Crea uno para organizar tus campañas y repetir estilos sin caos.</p>

            <div className="mt-5">
              <button
                onClick={openCreate}
                className="px-5 py-3 bg-white text-black rounded-xl font-semibold hover:bg-gray-200 transition"
              >
                Crear mi primer proyecto →
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="mt-10 text-sm text-gray-300 hover:text-white transition md:hidden">
        <button onClick={() => navigate("/brand")}>← Volver al inicio</button>
      </div>

      {/* ✅ MODAL: crear proyecto */}
      <Modal open={createOpen} title="Crea una carpeta para organizar" onClose={() => setCreateOpen(false)}>
        <div className="space-y-4">
          <div>
            <label className="block text-xs text-gray-400 mb-2">Nombre del proyecto</label>
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder={`Ej: Campaña Black Friday (${getDefaultBrandName() || "Mi marca"})`}
              className="w-full px-4 py-3 rounded-xl bg-black/30 border border-white/10 text-gray-100 outline-none focus:ring-2 focus:ring-indigo-500/40 text-sm"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-2">Descripción (opcional)</label>
            <textarea
              value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)}
              placeholder="Ej: Ideas, piezas, versiones, referencias, objetivo..."
              className="w-full min-h-[110px] px-4 py-3 rounded-xl bg-black/30 border border-white/10 text-gray-100 outline-none focus:ring-2 focus:ring-indigo-500/40 text-sm"
            />
          </div>

          <div className="mt-5 flex justify-end gap-2">
            <button
              onClick={() => setCreateOpen(false)}
              className="h-10 px-4 rounded-xl text-sm font-semibold border border-white/15 hover:bg-white/10 transition"
            >
              Cancelar
            </button>
            <button
              onClick={doCreate}
              className="h-10 px-4 rounded-xl text-sm font-semibold
                bg-gradient-to-r from-indigo-500 to-purple-500
                hover:from-indigo-400 hover:to-purple-400 transition shadow-lg shadow-indigo-500/30"
            >
              Crear →
            </button>
          </div>

          <div className="text-[11px] text-gray-600">{credits === null ? "" : `Saldo actual: ${credits} créditos.`}</div>
        </div>
      </Modal>

      {/* Modal rename (placeholder) */}
      <Modal open={renameOpen} title="Renombrar proyecto" onClose={() => setRenameOpen(false)}>
        <div className="space-y-4">
          <div className="text-[12px] text-gray-400">
            (Pendiente) Para renombrar necesitamos agregar endpoint PATCH /api/projects/:id en el backend.
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-2">Nuevo nombre</label>
            <input
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              placeholder="Escribe el nuevo nombre..."
              className="w-full px-4 py-3 rounded-xl bg-black/30 border border-white/10 text-gray-100 outline-none focus:ring-2 focus:ring-indigo-500/40 text-sm"
            />
          </div>

          <div className="mt-5 flex justify-end gap-2">
            <button
              onClick={() => setRenameOpen(false)}
              className="h-10 px-4 rounded-xl text-sm font-semibold border border-white/15 hover:bg-white/10 transition"
            >
              Cerrar
            </button>
          </div>
        </div>
      </Modal>
    </section>
  );
}

